<ng-container *ngIf="loading; else loaded">
  <div class="loading-state">
    <div class="loading-spinner">
      <i class="fa-solid fa-spinner fa-spin"></i>
    </div>
    <div class="loading-text">Loading test environment...</div>
  </div>
</ng-container>

<ng-template #loaded>
  <ng-container *ngIf="accounts; else notLoggedIn">
    <div class="test-page">
      <!-- Header -->
      <header class="page-header">
        <div class="header-content">
          <div class="header-title-row">
            <h1>Internal Test Page</h1>
            <button class="logout-btn" (click)="doLogout()">
              <i class="fa-solid fa-arrow-right-from-bracket"></i> Logout
            </button>
          </div>
          <p class="header-subtitle">Development environment for testing UI components with mock data.</p>
        </div>
        <nav class="section-nav">
          <a class="nav-pill" (click)="scrollTo('accounts-section')">
            <i class="fa-solid fa-users"></i> Accounts
            <span class="nav-count">{{ accounts.length }}</span>
          </a>
          <a class="nav-pill" (click)="scrollTo('issues-section')">
            <i class="fa-solid fa-circle-exclamation"></i> Issues
            <span class="nav-count">{{ issues.length }}</span>
          </a>
          <a class="nav-pill" (click)="scrollTo('nested-issues-section')">
            <i class="fa-solid fa-layer-group"></i> Nested Issues
            <span class="nav-count">{{ nestedIssues.length }}</span>
          </a>
          <a class="nav-pill" (click)="scrollTo('resources-section')">
            <i class="fa-solid fa-cube"></i> Resources
            <span class="nav-count">{{ statusResources.length }}</span>
          </a>
        </nav>
      </header>

      <!-- Accounts Section -->
      <section id="accounts-section" class="page-section">
        <div class="section-header clickable" (click)="toggleSection('accounts')">
          <h2>
            <i class="fa-solid fa-users section-icon"></i>
            Accounts
            <span class="section-count">{{ accounts.length }}</span>
          </h2>
          <i
            class="fa-solid section-chevron"
            [class.fa-chevron-down]="!expandedSections['accounts']"
            [class.fa-chevron-up]="expandedSections['accounts']"
          ></i>
        </div>
        <div class="section-body" *ngIf="expandedSections['accounts']">
          <div class="accounts-grid">
            <div class="account-card" *ngFor="let account of visibleAccounts">
              <div class="account-id">
                <span class="account-id-text">{{ account.accountId }}</span>
                <a class="copy-clipboard" [copy-clipboard]="account.accountId">
                  <i class="fa-utility fa-semibold fa-copy"></i>
                </a>
              </div>
              <div class="account-name" *ngIf="account.name">{{ account.name }}</div>
              <div class="account-actions">
                <a class="action-link" routerLink="/{{ account.accountId }}" target="_blank">
                  <i class="fa-solid fa-arrow-up-right-from-square"></i> Open
                </a>
                <a
                  class="action-link"
                  routerLink="/create_account/aws_accounts"
                  [queryParams]="{ createdAccountId: account.accountId, nativeAuthToken: 'fakeToken' }"
                >
                  <i class="fa-solid fa-plus"></i> Create App
                </a>
              </div>
            </div>
          </div>
          <button
            *ngIf="hasMoreAccounts"
            class="show-more-btn"
            (click)="showAllAccounts = !showAllAccounts; $event.stopPropagation()"
          >
            <i class="fa-solid" [class.fa-chevron-down]="!showAllAccounts" [class.fa-chevron-up]="showAllAccounts"></i>
            {{ showAllAccounts ? 'Show fewer' : 'Show all ' + accounts.length + ' accounts' }}
          </button>
        </div>
      </section>

      <!-- Issues Section -->
      <section id="issues-section" class="page-section">
        <div class="section-header clickable" (click)="toggleSection('issues')">
          <h2>
            <i class="fa-solid fa-circle-exclamation section-icon"></i>
            Issue Types
            <span class="section-count">{{ issues.length }}</span>
          </h2>
          <i
            class="fa-solid section-chevron"
            [class.fa-chevron-down]="!expandedSections['issues']"
            [class.fa-chevron-up]="expandedSections['issues']"
          ></i>
        </div>
        <div class="section-body" *ngIf="expandedSections['issues']">
          <p class="section-description">All mock issue types rendered with their respective components.</p>
          <issues-view [issues]="issues" [sync-pair]="syncPair" [application]="application"></issues-view>
        </div>
      </section>

      <!-- Nested Issues Section -->
      <section id="nested-issues-section" class="page-section">
        <div class="section-header clickable" (click)="toggleSection('nestedIssues')">
          <h2>
            <i class="fa-solid fa-layer-group section-icon"></i>
            Nested Issues
            <span class="section-count">{{ nestedIssues.length }}</span>
          </h2>
          <i
            class="fa-solid section-chevron"
            [class.fa-chevron-down]="!expandedSections['nestedIssues']"
            [class.fa-chevron-up]="expandedSections['nestedIssues']"
          ></i>
        </div>
        <div class="section-body" *ngIf="expandedSections['nestedIssues']">
          <p class="section-description">
            Issue grouping variations: single issue, 2 children, 3 children, 5 children, and standalone ungrouped
            issues.
          </p>
          <issues-view [issues]="nestedIssues" [sync-pair]="syncPair" [application]="application"></issues-view>
        </div>
      </section>

      <!-- Resources Section -->
      <section id="resources-section" class="page-section">
        <div class="section-header clickable" (click)="toggleSection('resources')">
          <h2>
            <i class="fa-solid fa-cube section-icon"></i>
            Resource Status
            <span class="section-count">{{ statusResources.length }}</span>
          </h2>
          <i
            class="fa-solid section-chevron"
            [class.fa-chevron-down]="!expandedSections['resources']"
            [class.fa-chevron-up]="expandedSections['resources']"
          ></i>
        </div>
        <div class="section-body" *ngIf="expandedSections['resources']">
          <p class="section-description">Mock resource statuses across all available status types.</p>
          <app-resources-view [resources]="statusResources" [tabType]="RecoveryTab"></app-resources-view>
        </div>
      </section>
    </div>
  </ng-container>
</ng-template>

<ng-template #notLoggedIn>
  <div class="not-logged-in">
    <div class="not-logged-in-card">
      <i class="fa-solid fa-lock lock-icon"></i>
      <h2>Not Logged In</h2>
      <p>You need to be authenticated to access the test page.</p>
      <a class="login-link" routerLink="" (click)="doLogout()">
        <i class="fa-solid fa-arrow-right-to-bracket"></i> Log in
      </a>
    </div>
  </div>
</ng-template>
